import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getEAProject, deleteEAProject } from '../services/ea-api'
import type { EAProject, EAProjectStatus, EAProjectPhase } from '../types/ea'

const glassPanel = {
  background: 'rgba(15, 23, 42, 0.6)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(100, 180, 255, 0.1)',
}

const VENDOR_COLORS: Record<string, string> = {
  Arista: '#2563eb',
  PaloAlto: '#dc2626',
  Nutanix: '#059669',
  Azure: '#0284c7',
  Cisco: '#7c3aed',
}

const STATUS_COLORS: Record<string, string> = {
  active: '#10b981',
  on_hold: '#f59e0b',
  complete: '#3b82f6',
  cancelled: '#ef4444',
}

const STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  on_hold: 'On Hold',
  complete: 'Complete',
  cancelled: 'Cancelled',
}

const PHASE_LABELS: Record<string, string> = {
  discovery: 'Discovery',
  design: 'Design',
  standards_gate: 'Standards Gate',
  deployment: 'Deployment',
  qc: 'QC Validation',
  deliverables: 'Deliverables',
  complete: 'Complete',
}

const ENGAGEMENT_LABELS: Record<string, string> = {
  consulting: 'Consulting',
  migration: 'Migration',
  greenfield: 'Greenfield',
  refresh: 'Refresh',
  assessment: 'Assessment',
  rfp_response: 'RFP Response',
}

const PHASE_ORDER: EAProjectPhase[] = ['discovery', 'design', 'standards_gate', 'deployment', 'qc', 'deliverables', 'complete']

type TabKey = 'overview' | 'pipeline' | 'artifacts' | 'qc' | 'deliverables' | 'log'

const TABS: { key: TabKey; label: string; icon: string }[] = [
  { key: 'overview', label: 'Overview', icon: 'fa-solid fa-eye' },
  { key: 'pipeline', label: 'Pipeline', icon: 'fa-solid fa-timeline' },
  { key: 'artifacts', label: 'Artifacts', icon: 'fa-solid fa-file-code' },
  { key: 'qc', label: 'QC', icon: 'fa-solid fa-clipboard-check' },
  { key: 'deliverables', label: 'Deliverables', icon: 'fa-solid fa-box-open' },
  { key: 'log', label: 'Log', icon: 'fa-solid fa-scroll' },
]

function StatusBadge({ status }: { status: EAProjectStatus }) {
  const color = STATUS_COLORS[status] || '#94a3b8'
  const label = STATUS_LABELS[status] || status
  return (
    <span
      className="px-3 py-1.5 text-sm font-medium rounded-full"
      style={{ background: `${color}22`, color, border: `1px solid ${color}44` }}
    >
      {label}
    </span>
  )
}

function PhaseBadge({ phase }: { phase: EAProjectPhase }) {
  const label = PHASE_LABELS[phase] || phase
  return (
    <span
      className="px-3 py-1.5 text-sm font-medium rounded-full"
      style={{ background: 'rgba(139, 92, 246, 0.15)', color: '#a78bfa', border: '1px solid rgba(139, 92, 246, 0.3)' }}
    >
      {label}
    </span>
  )
}

function VendorBadge({ vendor }: { vendor: string }) {
  const color = VENDOR_COLORS[vendor] || '#94a3b8'
  return (
    <span
      className="px-2.5 py-1 text-xs font-medium rounded-full text-white"
      style={{ background: color }}
    >
      {vendor}
    </span>
  )
}

function PhaseProgressBar({ currentPhase }: { currentPhase: EAProjectPhase }) {
  const currentIndex = PHASE_ORDER.indexOf(currentPhase)
  return (
    <div className="flex items-center space-x-1">
      {PHASE_ORDER.map((phase, i) => {
        const isComplete = i < currentIndex
        const isCurrent = i === currentIndex
        const color = isComplete ? '#10b981' : isCurrent ? '#60a5fa' : 'rgba(100, 116, 139, 0.3)'
        return (
          <div key={phase} className="flex items-center">
            <div
              className="w-3 h-3 rounded-full"
              style={{ background: color, boxShadow: isCurrent ? `0 0 8px ${color}` : 'none' }}
              title={PHASE_LABELS[phase]}
            ></div>
            {i < PHASE_ORDER.length - 1 && (
              <div
                className="w-6 h-0.5"
                style={{ background: isComplete ? '#10b981' : 'rgba(100, 116, 139, 0.2)' }}
              ></div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function OverviewTab({ project }: { project: EAProject }) {
  return (
    <div className="space-y-6">
      {/* Project Details Card */}
      <div className="rounded-xl p-6" style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.1)' }}>
        <h3 className="text-sm font-semibold uppercase tracking-wider mb-4" style={{ color: 'rgba(100, 116, 139, 0.6)' }}>
          Project Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>Project Code</p>
              <p className="text-sm font-mono" style={{ color: '#e2e8f0' }}>{project.project_code}</p>
            </div>
            <div>
              <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>Client</p>
              <p className="text-sm" style={{ color: '#e2e8f0' }}>{project.client_name}</p>
            </div>
            <div>
              <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>Engagement Type</p>
              <p className="text-sm" style={{ color: '#e2e8f0' }}>{ENGAGEMENT_LABELS[project.engagement_type] || project.engagement_type}</p>
            </div>
            <div>
              <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>SOW Number</p>
              <p className="text-sm" style={{ color: '#e2e8f0' }}>{project.sow_number || '--'}</p>
            </div>
            <div>
              <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>Project Lead</p>
              <p className="text-sm" style={{ color: '#e2e8f0' }}>{project.project_lead || '--'}</p>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>Created By</p>
              <p className="text-sm" style={{ color: '#e2e8f0' }}>{project.created_by}</p>
            </div>
            <div>
              <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>Created</p>
              <p className="text-sm" style={{ color: '#e2e8f0' }}>{new Date(project.created_at).toLocaleDateString()}</p>
            </div>
            <div>
              <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>Last Updated</p>
              <p className="text-sm" style={{ color: '#e2e8f0' }}>{new Date(project.updated_at).toLocaleDateString()}</p>
            </div>
            <div>
              <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>Target Completion</p>
              <p className="text-sm" style={{ color: '#e2e8f0' }}>
                {project.target_completion ? new Date(project.target_completion).toLocaleDateString() : '--'}
              </p>
            </div>
            {project.tags && project.tags.length > 0 && (
              <div>
                <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>Tags</p>
                <div className="flex flex-wrap gap-1.5">
                  {project.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-0.5 text-xs rounded-full"
                      style={{ background: 'rgba(100, 116, 139, 0.2)', color: 'rgba(148, 163, 184, 0.8)', border: '1px solid rgba(100, 116, 139, 0.3)' }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        {project.project_description && (
          <div className="mt-6 pt-4" style={{ borderTop: '1px solid rgba(100, 180, 255, 0.08)' }}>
            <p className="text-xs font-medium mb-2" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>Description</p>
            <p className="text-sm leading-relaxed" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
              {project.project_description}
            </p>
          </div>
        )}
      </div>

      {/* Vendor Status Summary */}
      <div className="rounded-xl p-6" style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.1)' }}>
        <h3 className="text-sm font-semibold uppercase tracking-wider mb-4" style={{ color: 'rgba(100, 116, 139, 0.6)' }}>
          Vendor Status
        </h3>
        {project.vendors.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {project.vendors.map((vendor) => {
              const color = VENDOR_COLORS[vendor] || '#94a3b8'
              return (
                <div
                  key={vendor}
                  className="rounded-xl p-4"
                  style={{ background: `${color}11`, border: `1px solid ${color}33` }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold" style={{ color }}>{vendor}</span>
                    <span
                      className="px-2 py-0.5 text-xs font-medium rounded-full"
                      style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10b981', border: '1px solid rgba(16, 185, 129, 0.3)' }}
                    >
                      {PHASE_LABELS[project.current_phase]}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 text-xs" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                    <i className="fa-solid fa-circle-check" style={{ color: `${color}88` }}></i>
                    <span>Pipeline ready</span>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>No vendors assigned to this project.</p>
        )}
      </div>

      {/* Recent Activity */}
      <div className="rounded-xl p-6" style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.1)' }}>
        <h3 className="text-sm font-semibold uppercase tracking-wider mb-4" style={{ color: 'rgba(100, 116, 139, 0.6)' }}>
          Recent Activity
        </h3>
        <div className="text-center py-8">
          <i className="fa-solid fa-clock-rotate-left text-2xl mb-3" style={{ color: 'rgba(100, 116, 139, 0.3)' }}></i>
          <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
            Activity will appear here once pipeline tasks are run.
          </p>
        </div>
      </div>
    </div>
  )
}

function ComingSoonTab({ tabName }: { tabName: string }) {
  return (
    <div className="rounded-xl p-12 text-center" style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.1)' }}>
      <div
        className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
        style={{ background: 'rgba(139, 92, 246, 0.1)', border: '1px solid rgba(139, 92, 246, 0.2)' }}
      >
        <i className="fa-solid fa-rocket text-3xl" style={{ color: 'rgba(167, 139, 250, 0.5)' }}></i>
      </div>
      <h3 className="text-lg font-semibold mb-2" style={{ color: '#e2e8f0' }}>{tabName} - Coming Soon</h3>
      <p style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
        This feature is under development and will be available in an upcoming release.
      </p>
    </div>
  )
}

export default function EAProjectDetail() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<TabKey>('overview')

  const { data: project, isLoading } = useQuery({
    queryKey: ['ea-project', projectId],
    queryFn: () => getEAProject(projectId!).then((res) => res.data),
    enabled: !!projectId,
  })

  const deleteMutation = useMutation({
    mutationFn: () => deleteEAProject(projectId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ea-projects'] })
      navigate('/ea/projects')
    },
  })

  const handleDelete = () => {
    if (confirm(`Delete project "${project?.project_name}"? This cannot be undone.`)) {
      deleteMutation.mutate()
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div
            className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(59, 130, 246, 0.1)', border: '2px solid rgba(59, 130, 246, 0.3)', boxShadow: '0 0 30px rgba(59, 130, 246, 0.3)' }}
          >
            <i className="fa-solid fa-spinner fa-spin text-2xl" style={{ color: '#60a5fa' }}></i>
          </div>
          <p style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Loading project...</p>
        </div>
      </div>
    )
  }

  if (!project) {
    return (
      <div className="text-center py-20">
        <div
          className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
          style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)' }}
        >
          <i className="fa-solid fa-exclamation-triangle text-3xl" style={{ color: '#ef4444' }}></i>
        </div>
        <h3 className="text-lg font-semibold mb-2" style={{ color: '#e2e8f0' }}>Project not found</h3>
        <p className="mb-6" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
          The requested project could not be found.
        </p>
        <Link
          to="/ea/projects"
          className="px-6 py-3 rounded-xl text-sm font-medium inline-flex items-center space-x-2 transition-all duration-200 hover:scale-105"
          style={{
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)',
            border: '1px solid rgba(96, 165, 250, 0.4)',
            color: '#e2e8f0',
          }}
        >
          <i className="fa-solid fa-arrow-left"></i>
          <span>Back to Projects</span>
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/ea/projects"
            className="w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 hover:scale-105"
            style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid rgba(100, 180, 255, 0.15)',
              color: 'rgba(148, 163, 184, 0.8)',
            }}
          >
            <i className="fa-solid fa-arrow-left"></i>
          </Link>
          <div>
            <div className="flex items-center space-x-3 mb-1">
              <h1 className="text-2xl font-bold" style={{ color: '#e2e8f0' }}>
                {project.project_name}
              </h1>
              <StatusBadge status={project.status} />
              <PhaseBadge phase={project.current_phase} />
            </div>
            <p className="text-sm font-mono" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
              {project.project_code}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => navigate(`/ea/projects/${projectId}/edit`)}
            className="px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 flex items-center space-x-2 hover:scale-105"
            style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid rgba(100, 180, 255, 0.15)',
              color: '#60a5fa',
            }}
          >
            <i className="fa-solid fa-pen"></i>
            <span>Edit</span>
          </button>
          <button
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 flex items-center space-x-2 hover:scale-105 disabled:opacity-50"
            style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
              color: '#ef4444',
            }}
          >
            <i className={`fa-solid ${deleteMutation.isPending ? 'fa-spinner fa-spin' : 'fa-trash'}`}></i>
            <span>Delete</span>
          </button>
        </div>
      </div>

      {/* Info Bar */}
      <div className="rounded-xl p-4" style={glassPanel}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <i className="fa-solid fa-building text-sm" style={{ color: 'rgba(148, 163, 184, 0.5)' }}></i>
              <span className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>{project.client_name}</span>
            </div>
            <div className="flex items-center space-x-2">
              <i className="fa-solid fa-briefcase text-sm" style={{ color: 'rgba(148, 163, 184, 0.5)' }}></i>
              <span className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>{ENGAGEMENT_LABELS[project.engagement_type] || project.engagement_type}</span>
            </div>
            {project.project_lead && (
              <div className="flex items-center space-x-2">
                <i className="fa-solid fa-user text-sm" style={{ color: 'rgba(148, 163, 184, 0.5)' }}></i>
                <span className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>{project.project_lead}</span>
              </div>
            )}
            <div className="flex items-center space-x-2">
              <i className="fa-solid fa-calendar text-sm" style={{ color: 'rgba(148, 163, 184, 0.5)' }}></i>
              <span className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
                {new Date(project.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {project.vendors.map((v) => (
              <VendorBadge key={v} vendor={v} />
            ))}
          </div>
        </div>
        {/* Phase progress bar */}
        <div className="mt-4 pt-3" style={{ borderTop: '1px solid rgba(100, 180, 255, 0.08)' }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>Phase Progress</span>
            <span className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>{PHASE_LABELS[project.current_phase]}</span>
          </div>
          <PhaseProgressBar currentPhase={project.current_phase} />
        </div>
      </div>

      {/* Tab Navigation */}
      <div
        className="rounded-xl overflow-hidden"
        style={{ ...glassPanel, padding: 0 }}
      >
        <div className="flex" style={{ borderBottom: '1px solid rgba(100, 180, 255, 0.1)' }}>
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className="px-5 py-3.5 text-sm font-medium flex items-center space-x-2 transition-all duration-200 relative"
              style={{
                color: activeTab === tab.key ? '#60a5fa' : 'rgba(148, 163, 184, 0.6)',
                background: activeTab === tab.key ? 'rgba(59, 130, 246, 0.08)' : 'transparent',
              }}
            >
              <i className={tab.icon}></i>
              <span>{tab.label}</span>
              {activeTab === tab.key && (
                <div
                  className="absolute bottom-0 left-0 right-0 h-0.5"
                  style={{ background: '#60a5fa' }}
                ></div>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'overview' && <OverviewTab project={project} />}
          {activeTab === 'pipeline' && <ComingSoonTab tabName="Pipeline" />}
          {activeTab === 'artifacts' && <ComingSoonTab tabName="Artifacts" />}
          {activeTab === 'qc' && <ComingSoonTab tabName="QC Validation" />}
          {activeTab === 'deliverables' && <ComingSoonTab tabName="Deliverables" />}
          {activeTab === 'log' && <ComingSoonTab tabName="Handover Log" />}
        </div>
      </div>
    </div>
  )
}
